/**
 * Template Preview Component
 * Mostra anteprima del template stile WhatsApp
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const TemplatePreview = ({ template, onClose }) => {
  const { t } = useTranslation();
  const [variables, setVariables] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize variables from template
  useEffect(() => {
    if (template?.variables) {
      const initialVars = {};
      template.variables.forEach(v => {
        initialVars[v.name] = v.example || '';
      });
      setVariables(initialVars);
    }
  }, [template]);

  // Fetch preview
  const fetchPreview = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/v1/templates/preview', {
        template_id: template.id,
        variables: variables
      });
      setPreview(response.data);
    } catch (err) {
      console.error('Preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (template) {
      fetchPreview();
    }
  }, [template, variables]);

  const handleVariableChange = (name, value) => {
    setVariables(prev => ({ ...prev, [name]: value }));
  };

  // Local preview (without API call)
  const getLocalPreview = () => {
    let body = template.body_text || '';
    let header = template.header_text || '';
    
    if (template.variables) {
      template.variables.forEach(v => {
        const placeholder = `{{${v.position}}}`;
        const value = variables[v.name] || `[${v.name}]`;
        body = body.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
        header = header.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
      });
    }
    
    return {
      header: header,
      body: body,
      footer: template.footer_text,
      buttons: template.buttons
    };
  };

  const displayPreview = preview || getLocalPreview();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>👁️ {t('templates.previewTitle')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {/* Template Info */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{template.name}</h3>
            <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.85rem', color: '#666' }}>
              <span>📍 {template.language.toUpperCase()}</span>
              <span>📁 {template.category}</span>
              <span className={`status-badge ${template.status?.toLowerCase()}`}>
                {template.status}
              </span>
            </div>
          </div>
          
          {/* Variables Input */}
          {template.variables && template.variables.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ fontSize: '0.9rem', marginBottom: '0.75rem', color: '#666' }}>
                {t('templates.preview.fillVariables')}
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {template.variables.map((variable) => (
                  <div key={variable.position} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                      {`{{${variable.position}}} ${variable.name}`}
                    </label>
                    <input
                      type="text"
                      value={variables[variable.name] || ''}
                      onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                      placeholder={variable.example}
                      style={{ padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* WhatsApp Style Preview */}
          <div className="whatsapp-preview">
            <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '0.8rem', color: '#666' }}>
              {t('templates.preview.phonePreview')}
            </div>
            
            <div className="whatsapp-message">
              {displayPreview.header && (
                <div className="header">{displayPreview.header}</div>
              )}
              
              <div className="body">
                {displayPreview.body}
              </div>
              
              {displayPreview.footer && (
                <div className="footer">{displayPreview.footer}</div>
              )}
              
              {displayPreview.buttons && displayPreview.buttons.length > 0 && (
                <div className="buttons">
                  {displayPreview.buttons.map((btn, i) => (
                    <button key={i}>
                      {btn.type === 'PHONE_NUMBER' && '📞 '}
                      {btn.type === 'URL' && '🔗 '}
                      {btn.text}
                    </button>
                  ))}
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '0.7rem', color: '#999' }}>
                  {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} ✓✓
                </span>
              </div>
            </div>
          </div>
          
          {/* Status Notice */}
          {template.status === 'REJECTED' && template.rejection_reason && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fee2e2', borderRadius: '8px', color: '#dc2626', fontSize: '0.85rem' }}>
              <strong>⚠️ {t('templates.preview.rejectionReason')}:</strong><br />
              {template.rejection_reason}
            </div>
          )}
          
          {template.status === 'PENDING' && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', color: '#d97706', fontSize: '0.85rem' }}>
              ⏳ {t('templates.preview.pendingNotice')}
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-sync">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
